<script setup>
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';

import ComboBox from 'next/combobox/ComboBox.vue';
import Input from 'next/input/Input.vue';
import { clampTemperature, findLlmConfig } from '../helpers/mainAgentConfig';

const props = defineProps({
  modelValue: { type: Object, required: true },
  options: { type: Array, default: () => [] },
  label: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const selectedOption = computed(() =>
  findLlmConfig(props.modelValue.llm_config, props.options)
);
const temperatureRange = computed(
  () => selectedOption.value?.temperature ?? null
);
const comboOptions = computed(() =>
  props.options
    .filter(option => !option.deprecated)
    .map(option => ({ value: option.name, label: option.name }))
);

const rules = {
  llm_config: {
    required,
    supported: value =>
      !props.options.length ||
      comboOptions.value.some(option => option.value === value),
  },
  temperature: {
    required: requiredIf(() => Boolean(temperatureRange.value)),
    inRange: value => {
      if (!temperatureRange.value || value === null || value === '')
        return true;
      const { min, max } = temperatureRange.value;
      return Number(value) >= min && Number(value) <= max;
    },
  },
};

const v$ = useVuelidate(rules, toRef(props, 'modelValue'));

const temperatureMessage = computed(() => {
  if (!temperatureRange.value) return '';
  if (v$.value.temperature.$error) {
    return t(
      'WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.TEMPERATURE.ERROR',
      temperatureRange.value
    );
  }
  return t(
    'WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.TEMPERATURE.HINT',
    temperatureRange.value
  );
});

const onModelChange = name => {
  const option = findLlmConfig(name, props.options);
  emit('update:modelValue', {
    llm_config: name,
    temperature: option?.temperature
      ? clampTemperature(props.modelValue.temperature, option.temperature)
      : null,
  });
};

const onTemperatureChange = value => {
  emit('update:modelValue', {
    ...props.modelValue,
    temperature: value === '' ? null : value,
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-sm font-medium text-n-slate-12">{{
        label
      }}</span>
      <ComboBox
        :model-value="modelValue.llm_config"
        :options="comboOptions"
        :display-label="modelValue.llm_config"
        :placeholder="
          t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.LLM_CONFIG.PLACEHOLDER')
        "
        :search-placeholder="
          t(
            'WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.LLM_CONFIG.SEARCH_PLACEHOLDER'
          )
        "
        :empty-state="
          t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.LLM_CONFIG.EMPTY_STATE')
        "
        :has-error="v$.llm_config.$error"
        :message="
          v$.llm_config.$error
            ? t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.LLM_CONFIG.ERROR')
            : ''
        "
        @update:model-value="onModelChange"
      />
    </div>
    <Input
      v-if="temperatureRange"
      :model-value="modelValue.temperature ?? ''"
      type="number"
      step="0.1"
      :label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.MAIN_AGENT.TEMPERATURE.LABEL')"
      :message="temperatureMessage"
      :message-type="v$.temperature.$error ? 'error' : 'info'"
      @update:model-value="onTemperatureChange"
      @blur="v$.temperature.$touch"
    />
  </div>
</template>
